import React, { useState, useEffect } from 'react';
import { DEFAULT_AVATAR } from '../../constants';
import {
  Container,
  Grid,
  Paper,
  Typography,
  Box,
  Avatar,
  IconButton,
  Divider,
  Stack,
  Chip,
  Button,
  useTheme,
  useMediaQuery,
  Skeleton,
  alpha,
} from '@mui/material';
import {
  Share as ShareIcon,
  Report as ReportIcon,
  LocationOn,
  ArrowBack as ArrowBackIcon,
  Message as MessageIcon,
} from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { GetByPersonId } from '../../services/PersonService';
import ChatRequestButton from '../../components/Chat/ChatRequestButton';
import ComplaintDialog from '../../components/Complaint/ComplaintDialog';
import { useAuth } from '../../contexts/AuthContext';
import LoginRequiredModal from '../../components/common/LoginRequiredModal';
import VerifiedBadge from '../../components/common/VerifiedBadge';
import { Helmet } from 'react-helmet-async';
import { toast } from 'react-toastify';

const RoommateDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { currentUser, checkAuth } = useAuth();

  const [isComplaintOpen, setComplaintOpen] = useState(false);
  const [roommate, setRoommate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [loginOpen, setLoginOpen] = useState(false);
  const [loginAction, setLoginAction] = useState('');

  const isLoggedIn = !!currentUser || checkAuth?.();

  useEffect(() => {
    const fetchRoommate = async () => {
      try {
        setLoading(true);
        const response = await GetByPersonId(id);
        setRoommate(response?.data || null);
        if (!response?.data) setError(t('roommate.notFound'));
      } catch (err) {
        setError(err.message || t('roommate.error'));
      } finally {
        setLoading(false);
      }
    };
    fetchRoommate();
  }, [id, t, currentUser]);

  const requireLogin = (action) => {
    setLoginAction(action);
    setLoginOpen(true);
  };

  const handleShare = async () => {
    try {
      if (navigator.share) {
        await navigator.share({
          title: `${roommate?.name || ''} ${roommate?.lastName || ''}`,
          text: t('roommate.shareText'),
          url: window.location.href,
        });
      } else {
        await navigator.clipboard.writeText(window.location.href);
        toast.success(t('advertisement.linkCopied') || 'Link kopyalandı');
      }
    } catch (err) {
      // ignore cancel
    }
  };

  const handleReport = () => {
    if (!isLoggedIn) {
      requireLogin(t('roommate.report'));
      return;
    }
    setComplaintOpen(true);
  };

  const handleBack = () => navigate(-1);

  const genderLabel =
    roommate?.gender === 201 || roommate?.gender === '201'
      ? t('roommateFilter.male')
      : roommate?.gender === 104 || roommate?.gender === '104'
        ? t('roommateFilter.female')
        : null;

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Skeleton variant="rectangular" height={220} sx={{ borderRadius: 3, mb: 2 }} />
        <Skeleton height={180} sx={{ borderRadius: 3 }} />
      </Container>
    );
  }

  if (error || !roommate) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Paper elevation={0} sx={{ p: 4, textAlign: 'center', borderRadius: 3, border: `1px solid ${theme.palette.divider}` }}>
          <Typography variant="h6" color="error" gutterBottom>
            {error || t('roommate.notFound')}
          </Typography>
          <Button variant="contained" onClick={handleBack} startIcon={<ArrowBackIcon />} sx={{ mt: 2 }}>
            {t('common.back')}
          </Button>
        </Paper>
      </Container>
    );
  }

  return (
    <Box
      sx={{
        minHeight: '70vh',
        pb: 6,
        background:
          theme.palette.mode === 'dark'
            ? theme.palette.background.default
            : `linear-gradient(180deg, ${alpha(theme.palette.primary.main, 0.05)} 0%, ${theme.palette.background.default} 260px)`,
      }}
    >
      <Helmet>
        <title>
          {[roommate.name, roommate.lastName].filter(Boolean).join(' ')} | OdaZen
        </title>
      </Helmet>

      <Container maxWidth="lg" sx={{ pt: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2.5 }}>
          <IconButton
            onClick={handleBack}
            sx={{ bgcolor: 'background.paper', border: `1px solid ${theme.palette.divider}` }}
          >
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h5" fontWeight={800}>
            {t('roommate.profileDetails')}
          </Typography>
        </Box>

        <Grid container spacing={3}>
          <Grid item xs={12} md={8}>
            <Paper
              elevation={0}
              sx={{
                borderRadius: 3,
                overflow: 'hidden',
                border: `1px solid ${theme.palette.divider}`,
              }}
            >
              <Box
                sx={{
                  height: isMobile ? 120 : 150,
                  background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
                  position: 'relative',
                }}
              >
                <Box
                  sx={{
                    position: 'absolute',
                    inset: 0,
                    background:
                      'radial-gradient(circle at 15% 20%, rgba(255,255,255,0.2), transparent 40%)',
                  }}
                />
              </Box>

              <Box sx={{ px: { xs: 2.5, md: 3 }, pb: 3, mt: -6 }}>
                <Avatar
                  src={roommate.profile || DEFAULT_AVATAR}
                  alt={`${roommate.name || ''} ${roommate.lastName || ''}`}
                  sx={{
                    width: 104,
                    height: 104,
                    border: `4px solid ${theme.palette.background.paper}`,
                    boxShadow: `0 12px 28px ${alpha(theme.palette.primary.main, 0.28)}`,
                    mb: 1.5,
                  }}
                >
                  {(roommate.name || '?').charAt(0)}
                </Avatar>

                <Typography variant="h4" fontWeight={800} sx={{ fontSize: { xs: '1.5rem', md: '1.9rem' } }}>
                  {[roommate.name, roommate.lastName].filter(Boolean).join(' ')}
                </Typography>

                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 1.25 }}>
                  <VerifiedBadge verified={roommate.isIdentityVerified} variant="chip" />
                  {roommate.age && (
                    <Chip label={`${roommate.age}`} size="small" color="primary" variant="outlined" />
                  )}
                  {genderLabel && (
                    <Chip label={genderLabel} size="small" sx={{ bgcolor: alpha(theme.palette.primary.main, 0.08) }} />
                  )}
                  <Chip
                    icon={<LocationOn sx={{ fontSize: '16px !important' }} />}
                    label={[roommate.districtName, roommate.cityName].filter(Boolean).join(', ') || '—'}
                    size="small"
                    variant="outlined"
                  />
                </Stack>

                <Divider sx={{ my: 2.5 }} />

                <Typography variant="h6" fontWeight={700} gutterBottom>
                  {t('roommate.description')}
                </Typography>
                <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.8, whiteSpace: 'pre-line' }}>
                  {roommate.biography || t('roommate.noBiography')}
                </Typography>
              </Box>
            </Paper>
          </Grid>

          <Grid item xs={12} md={4}>
            <Paper
              elevation={0}
              sx={{
                p: 3,
                borderRadius: 3,
                border: `1px solid ${theme.palette.divider}`,
                position: 'sticky',
                top: 96,
              }}
            >
              <Typography variant="subtitle2" color="text.secondary" fontWeight={700} sx={{ mb: 1.5 }}>
                {t('roommate.contact')}
              </Typography>

              {isLoggedIn ? (
                <ChatRequestButton
                  userId={roommate.id}
                  pid={roommate.id}
                  ptype={2}
                  fullWidth
                  variant="contained"
                  startIcon={<MessageIcon />}
                  sx={{ mb: 1.5, py: 1.2 }}
                />
              ) : (
                <Button
                  fullWidth
                  variant="contained"
                  startIcon={<MessageIcon />}
                  onClick={() => requireLogin(t('roommate.contact'))}
                  sx={{ mb: 1.5, py: 1.2 }}
                >
                  {t('roommate.contact')}
                </Button>
              )}

              <Stack direction="row" spacing={1}>
                <Button fullWidth variant="outlined" startIcon={<ShareIcon />} onClick={handleShare}>
                  {t('roommate.share')}
                </Button>
                <Button
                  fullWidth
                  variant="outlined"
                  color="error"
                  startIcon={<ReportIcon />}
                  onClick={handleReport}
                >
                  {t('roommate.report')}
                </Button>
              </Stack>

              <Divider sx={{ my: 2.5 }} />

              <Typography variant="subtitle2" color="text.secondary" fontWeight={700} sx={{ mb: 1 }}>
                {t('roommate.location')}
              </Typography>
              <Box
                sx={{
                  p: 1.75,
                  borderRadius: 2,
                  bgcolor: alpha(theme.palette.primary.main, 0.04),
                  border: `1px solid ${theme.palette.divider}`,
                  display: 'flex',
                  gap: 1,
                  alignItems: 'flex-start',
                }}
              >
                <LocationOn color="primary" fontSize="small" />
                <Typography variant="body2">
                  {[roommate.districtName, roommate.cityName, roommate.countryName]
                    .filter(Boolean)
                    .join(', ') || '—'}
                </Typography>
              </Box>

              {!isLoggedIn && (
                <Box
                  sx={{
                    mt: 2.5,
                    p: 1.5,
                    borderRadius: 2,
                    bgcolor: alpha(theme.palette.primary.main, 0.06),
                    border: `1px dashed ${alpha(theme.palette.primary.main, 0.35)}`,
                  }}
                >
                  <Typography variant="caption" color="text.secondary">
                    {t('auth.loginRequired.body')}
                  </Typography>
                </Box>
              )}
            </Paper>
          </Grid>
        </Grid>
      </Container>

      <ComplaintDialog
        open={isComplaintOpen}
        onClose={() => setComplaintOpen(false)}
        pid={roommate.id}
        type={2}
      />

      <LoginRequiredModal
        open={loginOpen}
        onClose={() => setLoginOpen(false)}
        actionLabel={loginAction}
      />
    </Box>
  );
};

export default RoommateDetail;
